import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
} from "@mui/material";
import Grid from "./utils/CompatGrid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getTemplates,
  deleteTemplate,
  createTemplate,
} from "./api/labelStudioApi";
import LabelCanvas from "./components/LabelCanvas";
import { LABEL_SIZE_PRESETS, PAPER_TYPES } from "./utils/labelSizes";

const TemplatesList = () => {
  const [templates, setTemplates] = useState([]);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [sizePreset, setSizePreset] = useState(LABEL_SIZE_PRESETS[2].key);
  const [customWidth, setCustomWidth] = useState(60);
  const [customHeight, setCustomHeight] = useState(40);
  const [paperType, setPaperType] = useState("INDIVIDUAL_STICKER");
  const navigate = useNavigate();

  const load = async () => {
    try {
      setTemplates(await getTemplates());
    } catch (err) {
      toast.error("Failed to load templates");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await deleteTemplate(id);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await createTemplate({
        ...template,
        id: undefined,
        name: `${template.name} (Copy)`,
      });
      toast.success("Template duplicated");
      load();
    } catch (err) {
      toast.error("Failed to duplicate template");
    }
  };

  const handleCreateNew = () => {
    const preset = LABEL_SIZE_PRESETS.find((p) => p.key === sizePreset);
    const widthMm = sizePreset === "custom" ? customWidth : preset.widthMm;
    const heightMm = sizePreset === "custom" ? customHeight : preset.heightMm;
    navigate("/label-studio/templates/new", {
      state: { newTemplateConfig: { widthMm, heightMm, paperType } },
    });
  };

  const parseElements = (t) => {
    try {
      return t.elementsJson ? JSON.parse(t.elementsJson) : [];
    } catch (e) {
      return [];
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Label Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewDialogOpen(true)}
        >
          New Template
        </Button>
      </Box>

      <Grid container spacing={2}>
        {templates.map((t) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: "#f1f3f5",
                  p: 2,
                  minHeight: 140,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ transform: "scale(0.6)" }}>
                  <LabelCanvas
                    widthMm={t.widthMm}
                    heightMm={t.heightMm}
                    backgroundColor={t.backgroundColor}
                    backgroundImageUrl={t.backgroundImageUrl}
                    backgroundImageOpacity={t.backgroundImageOpacity}
                    borderColor={t.borderColor}
                    borderWidth={t.borderWidth}
                    elements={parseElements(t)}
                  />
                </Box>
              </Box>
              <CardContent>
                <Typography fontWeight={600}>{t.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.widthMm} x {t.heightMm} mm &middot;{" "}
                  {t.paperType?.replace(/_/g, " ")}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <IconButton size="small" onClick={() => handleDuplicate(t)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/label-studio/templates/${t.id}/edit`)
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(t.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {templates.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary" align="center">
              No templates yet. Click "New Template" to design your first label.
            </Typography>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>New Label Template</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            select
            label="Label Size"
            value={sizePreset}
            onChange={(e) => setSizePreset(e.target.value)}
          >
            {LABEL_SIZE_PRESETS.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {sizePreset === "custom" && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Width (mm)"
                type="number"
                value={customWidth}
                onChange={(e) => setCustomWidth(Number(e.target.value))}
                fullWidth
              />
              <TextField
                label="Height (mm)"
                type="number"
                value={customHeight}
                onChange={(e) => setCustomHeight(Number(e.target.value))}
                fullWidth
              />
            </Box>
          )}
          <TextField
            select
            label="Paper Type"
            value={paperType}
            onChange={(e) => setPaperType(e.target.value)}
          >
            {PAPER_TYPES.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateNew}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesList;
